import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

export interface CodeChunkDialogResult {
  title: string;
  codeLanguage: string;
}

@Component({
  selector: 'app-code-chunk-dialog',
  standalone: true,
  imports: [TranslateModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule, FormsModule],
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ 'CreateChunk.CodeChunk' | translate }}</h2>
          <p class="subtitle">{{ 'CreateChunk.CodeSubtitle' | translate }}</p>
        </div>
        <button mat-icon-button (click)="dialogRef.close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content pt-4">
        <div class="w-100 mb-3">
          <mat-label class="form-label mb-1">{{ 'CreateChunk.CodeTitle' | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
            <input matInput [(ngModel)]="title" (keydown.enter)="submit()" [placeholder]="'CreateChunk.CodeTitlePlaceholder' | translate" autofocus />
          </mat-form-field>
        </div>

        <div class="w-100 mb-3">
          <mat-label class="form-label mb-1">{{ 'CreateChunk.CodeLanguage' | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
            <mat-select [(ngModel)]="codeLanguage">
              @for (lang of codeLanguages; track lang.value) {
                <mat-option [value]="lang.value">{{ lang.viewValue }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="footer">
        <div class="right-actions ml-auto">
          <button mat-button (click)="dialogRef.close()">{{ 'Global.Cancel' | translate }}</button>
          <button mat-flat-button color="primary" (click)="submit()" [disabled]="!title.trim()">{{ 'Global.Insert' | translate }}</button>
        </div>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeChunkDialogComponent {
  dialogRef = inject(MatDialogRef<CodeChunkDialogComponent>);

  title = '';
  codeLanguage = 'javascript';

  codeLanguages = [
    { value: 'javascript', viewValue: 'JavaScript' },
    { value: 'python', viewValue: 'Python' },
    { value: 'clike', viewValue: 'C++' },
    { value: 'rust', viewValue: 'Rust' },
    { value: 'css', viewValue: 'CSS' },
    { value: 'java', viewValue: 'Java' },
    { value: 'csharp', viewValue: 'C#' },
    { value: 'php', viewValue: 'PHP' },
    { value: 'go', viewValue: 'Go' },
    { value: 'ruby', viewValue: 'Ruby' },
    { value: 'html', viewValue: 'HTML' },
    { value: 'vb', viewValue: 'Visual Basic' },
    { value: 'typescript', viewValue: 'TypeScript' },
    { value: 'sql', viewValue: 'SQL' },
    { value: 'bash', viewValue: 'Bash' },
    { value: 'markdown', viewValue: 'Markdown' },
    { value: 'mermaid', viewValue: 'Mermaid' },
  ];

  submit() {
    if (!this.title.trim()) return;
    this.dialogRef.close({ title: this.title.trim(), codeLanguage: this.codeLanguage });
  }
}